import * as actions from './shared-environment-info.actions';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable, inject } from '@angular/core';
import { SpxStorage, SpxStorageKeyEnum } from '@softpak/components/spx-storage';
import { SpxToasterAutoCloseSpeedEnum, spxToasterActions } from '@softpak/components/spx-toaster';
import { captureMessage, getCurrentScope } from '@sentry/angular';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { ApiCntMethod } from 'src/app/api/api-methods.cnt';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Observable } from 'rxjs';
import { SharedEnvironmentInfoI } from 'src/app/shared/models/shared/shared-environment-info.interface';
import { SharedErrorObjectI } from 'src/app/shared/models/shared/shared-error-object.interface';
import { SharedGenericService } from 'src/app/shared/services/shared-generic.service';
import { loadingEnvironmentInfo } from 'src/app/shared/_config/i18n/i18n-interface';
import { spxNavigationActions } from '@softpak/components/spx-navigation';
import { spxSpinnerActions } from '@softpak/components/spx-spinner';
import { statsConnectActions } from '../stats-connect';

@Injectable()
export class SharedEnvironmentInfoEffects {
    private readonly actions$ = inject(Actions);
    private readonly sharedGenericService = inject(SharedGenericService);


    load$ = createEffect(() => this.actions$.pipe(
        ofType(actions.load),
        mergeMap(_action =>
          {
            // TODO replace this with the infoBE call when every customer is on 25.01 or higher.
            return this.sharedGenericService.invoke(ApiCntMethod.sharedEnvironmentInformation).pipe(
                map((data: SharedEnvironmentInfoI) => actions.received({
                    data
                })),
                catchError((error: SharedErrorObjectI) => [
                    actions.error({
                        messageText: error.message
                    }),
                ])
            );
            
          }  
    )));

    afterReceived$: Observable<any> = createEffect(() => this.actions$.pipe(
        ofType(actions.received),
        tap(async () => {
            SpxStorage.setSetting(SpxStorageKeyEnum.platform, Capacitor.getPlatform());

            if (!SpxStorage.getSetting(SpxStorageKeyEnum.randomDeviceId)) {
                SpxStorage.setSetting(SpxStorageKeyEnum.randomDeviceId, Math.floor(Math.random() * (99999999 - 0 + 1) + 99999999).toString())
            }

            if (Capacitor.getPlatform() !== 'web') {
                const appInfo = await App.getInfo();
                const info = await Device.getInfo();
                SpxStorage.setSetting(SpxStorageKeyEnum.bundleVersion, appInfo.version);

                if (info.androidSDKVersion) {
                    SpxStorage.setSetting(SpxStorageKeyEnum.platformVersion, `WEBVIEW ${info.webViewVersion}`);
                }
                if (info.androidSDKVersion) {
                    SpxStorage.setSetting(SpxStorageKeyEnum.platformVersion, `ANDROID ${info.androidSDKVersion}`);
                }
                if (info.iOSVersion) {
                    SpxStorage.setSetting(SpxStorageKeyEnum.platformVersion, `iOS ${info.iOSVersion}`);
                }
            }
        }),
        mergeMap(action => {
            // TODO verwijder dit later weer. Dit is slechts logging om te kijken hoevaak de app zichzelf herstart bij DR Depots. 
            if (action.data.customerCode === 'Doorn') {
                captureMessage(`[SHARED / Env Loaded] ${action.data.customerCode} / ${action.data.userid}.`, {
                    level: 'log'
                });
            }

            SpxStorage.setSetting(SpxStorageKeyEnum.username, action.data.userid);

            getCurrentScope()
                .setUser({ username: action.data.userid })
                .setTag('brand', action.data.customerCode)
                .setTag('backend_patch', action.data.patchNr)
                .setTag('backend_release', action.data.versionNr);

            return [
                spxSpinnerActions.hide({
                    action: loadingEnvironmentInfo
                }),
                spxNavigationActions.update({
                  accessList: action.data.accessAllowed,
                  signedIn: true,
                }),
                statsConnectActions.load({
                    query: {
                        app: 'orders',
                        bundleVersion: SpxStorage.getSetting(SpxStorageKeyEnum.bundleVersion),
                        customer: SpxStorage.getSetting(SpxStorageKeyEnum.brand),
                        liveUpdate: SpxStorage.getSetting(SpxStorageKeyEnum.liveUpdate),
                        liveUpdateChannel: SpxStorage.getSetting(SpxStorageKeyEnum.liveUpdateChannel),
                        patch: action.data.patchNr.toString(),
                        platform: SpxStorage.getSetting(SpxStorageKeyEnum.platform),
                        platformVersion: SpxStorage.getSetting(SpxStorageKeyEnum.platformVersion),
                        randomDeviceId: SpxStorage.getSetting(SpxStorageKeyEnum.randomDeviceId),
                        release: action.data.versionNr,
                        username: action.data.userid,
                    }
                }),
            ];
        }))
    );

    afterReset$: Observable<any> = createEffect(() => this.actions$.pipe(
        ofType(actions.reset),
        mergeMap(() => [
            spxNavigationActions.update({
              accessList: [],
              signedIn: false
            }),
        ]))
    );

    onError$: Observable<any> = createEffect(() => this.actions$.pipe(
        ofType(actions.error),
        mergeMap(action => [
             spxSpinnerActions.hide({
                action: loadingEnvironmentInfo
            }),
            spxToasterActions.createError({
                autoClose: SpxToasterAutoCloseSpeedEnum.DEFAULT,
                messageText: action.messageText,
            }),
        ]))
    );

     afterLoad$: Observable<any> = createEffect(() => this.actions$.pipe(
        ofType(actions.load),
        mergeMap(() => [
            spxSpinnerActions.show({
                action: loadingEnvironmentInfo
            }),
        ]))
    );

    afterError$: Observable<any> = createEffect(() => this.actions$.pipe(
        ofType(actions.error),
        mergeMap(() => [
            spxSpinnerActions.hide({
                action: loadingEnvironmentInfo
            }),
        ]))
    );
}
